import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import cn from "classnames";
import { DocLeadAnchor, InvestigationLead } from "../../../db/schema";
import { usePdfViewer } from "../../../hooks/usePdfViewer";
import PdfFloatingNav from "../../Forms/PdfFloatingNav";
import LeadFlagPopover, { LeadHover } from "../LeadFlagPopover";
import { FlagBox, HiddenFlagsChip, useHiddenFlags } from "./flagOverlay";

export type PhotoViewerHandle = { goToPage: (page: number) => void };

const PHOTO_MIN_SCALE = 0.05;

function anchorOf(l: InvestigationLead): DocLeadAnchor | null {
  return (l.doc_anchor as DocLeadAnchor | null) || null;
}

/**
 * Evidence photo viewer. Same zoom hook and floating controls as the PDF,
 * Forms, and Redactions viewers (`usePdfViewer` + `PdfFloatingNav`): the
 * image's natural size plays the role of the page viewport, so zoom in/out,
 * fit, and space-to-pan behave identically. Fits to the pane on load, since
 * photo sizes vary far more than paper does.
 *
 * Leads carrying doc_anchor boxes (Textract line geometry for quoted text,
 * or the model's own region for a visual finding — both normalized 0-1, so
 * they scale with the image for free) are painted as severity-tinted
 * highlights, exactly as the PDF viewer paints them; hover shows the flag,
 * and the rail's cards drive the deeper "active" tint through activeLeadId.
 * Overlapping boxes can be hidden one at a time (eye toggle) to reach the
 * ones underneath; a hidden box keeps a dashed outline that restores it.
 */
const PhotoViewer = forwardRef<
  PhotoViewerHandle,
  {
    url: string;
    alt: string;
    onLoadError?: () => void;
    leads?: InvestigationLead[];
    activeLeadId?: string | null;
    onHoverLeads?: (ids: string[]) => void;
  }
>(({ url, alt, onLoadError, leads = [], activeLeadId, onHoverLeads }, ref) => {
  // Photos run far larger than paper; let fit go well below the PDF floor.
  // Click-and-drag panning is on whenever the image is larger than the pane
  // (zoomed in). When it all fits there is nothing to drag, so the cursor and
  // clicks stay normal. Space-to-pan still works either way.
  const [canPan, setCanPan] = useState(false);
  const pdf = usePdfViewer({
    storageKey: "investigationPhotoZoom",
    minScale: PHOTO_MIN_SCALE,
    selectedTool: canPan ? "pan" : null,
  });
  const { scale } = pdf;
  const { width: imgW, height: imgH } = pdf.pdfDimensions;
  useEffect(() => {
    const measure = () => {
      const c = pdf.containerRef.current;
      if (c) setCanPan(c.scrollWidth > c.clientWidth + 1 || c.scrollHeight > c.clientHeight + 1);
    };
    measure(); // runs after the new zoom has been laid out
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [scale, imgW, imgH, pdf.pageReady, pdf.containerRef]);
  const [failed, setFailed] = useState(false);
  const [hoverFlag, setHoverFlag] = useState<LeadHover | null>(null);
  const { hidden, hide, restore, reset } = useHiddenFlags();

  // One "page": a rail click just brings the image back into view.
  useImperativeHandle(ref, () => ({
    goToPage: () => pdf.containerRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" }),
  }));

  const onImageLoad = (img: HTMLImageElement) => {
    const { naturalWidth: width, naturalHeight: height } = img;
    pdf.onDocumentLoadSuccess({ numPages: 1 });
    pdf.onPageLoadSuccess({ getViewport: () => ({ width, height }) });
    // Fit on load: zoomFit reads pdfDimensions from state, so compute the
    // same width fit here rather than wait a render.
    const container = pdf.containerRef.current;
    if (container && width) pdf.setScale((container.clientWidth - 64) / width);
    pdf.setPageReady(true);
  };

  const flagged = leads.filter((l) => l.status !== "dismissed" && anchorOf(l)?.boxes?.length);
  const showFlags = (ls: InvestigationLead[], el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const below = r.bottom + 220 < window.innerHeight;
    setHoverFlag({ leads: ls, x: Math.min(r.left, window.innerWidth - 400), y: below ? r.bottom + 6 : r.top - 6, below });
    onHoverLeads?.(ls.map((f) => f.id));
  };
  const clearFlags = () => {
    setHoverFlag(null);
    onHoverLeads?.([]);
  };

  if (failed) {
    return <div className="h-full flex items-center justify-center text-base-content/60">Failed to load photo.</div>;
  }
  const { width, height } = pdf.pdfDimensions;
  return (
    <div className="relative h-full">
      <div
        ref={pdf.containerRef}
        tabIndex={-1}
        className={cn("h-full overflow-auto bg-base-300 p-8 outline-none", pdf.panCursorClass)}
        onMouseDown={pdf.handleContainerMouseDown}
        onMouseMove={pdf.handleContainerMouseMove}
        onMouseUp={pdf.handleContainerMouseEnd}
        onMouseLeave={pdf.handleContainerMouseEnd}
      >
        {/* Sized like the PDF page box so the percentage-positioned boxes
            land on the pixels at any zoom. */}
        <div
          ref={pdf.canvasRef}
          className={cn("relative mx-auto shadow-xl select-none transition-opacity duration-200", pdf.panCursorClass)}
          style={{ width: width ? width * scale : undefined, height: height ? height * scale : undefined, opacity: pdf.pageReady ? 1 : 0 }}
        >
          <img
            src={url}
            alt={alt}
            draggable={false}
            onLoad={(e) => onImageLoad(e.currentTarget)}
            onError={() => {
              setFailed(true);
              onLoadError?.();
            }}
            className="block w-full h-full rounded-sm"
          />
          {flagged.map((l) =>
            (anchorOf(l)?.boxes || []).map((b, bi) => (
              <FlagBox
                key={`${l.id}-${bi}`}
                severity={l.severity}
                active={l.id === activeLeadId}
                hidden={hidden.has(l.id)}
                control={bi === 0}
                onHide={() => hide(l.id)}
                onRestore={() => restore(l.id)}
                onMouseEnter={(el) => showFlags([l], el)}
                onMouseLeave={clearFlags}
                style={{
                  left: `${(b.x - 0.004) * 100}%`,
                  top: `${(b.y - 0.004) * 100}%`,
                  width: `${(b.w + 0.008) * 100}%`,
                  height: `${(b.h + 0.008) * 100}%`,
                }}
              />
            ))
          )}
        </div>
        {!pdf.pageReady && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <span className="loading loading-spinner loading-md text-base-content/30" />
          </div>
        )}
      </div>
      {pdf.pageReady && (
        <PdfFloatingNav
          currentPage={1}
          numPages={1}
          showPages={false}
          scale={scale}
          onPrevPage={pdf.prevPage}
          onNextPage={pdf.nextPage}
          onZoomIn={pdf.zoomIn}
          onZoomOut={pdf.zoomOut}
          onZoomFit={pdf.zoomFit}
          minScale={PHOTO_MIN_SCALE}
          maxScale={3}
        />
      )}
      <HiddenFlagsChip count={hidden.size} onReset={reset} />
      {hoverFlag && <LeadFlagPopover hover={hoverFlag} />}
    </div>
  );
});
PhotoViewer.displayName = "PhotoViewer";

export default PhotoViewer;
